import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Bell, X, CheckCircle } from '@phosphor-icons/react';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../lib/api';
import { toast } from 'sonner';

const POLL_INTERVAL_MS = 60_000; // 1 min
const ELIGIBLE_ROLES = ['admin', 'owner', 'assistant', 'accountant', 'staff', 'sub_staff'];

/**
 * NotificationBell — fixed top-right bell icon with unread count badge + dropdown.
 * Visible to admin/owner/assistant/accountant/staff/sub_staff. Polls /api/notifications every 60s.
 * Critical (red) notifications pulse to draw attention.
 */
export default function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const fetchNotifs = useCallback(async () => {
    if (!user || !ELIGIBLE_ROLES.includes(user.role)) return;
    try {
      const { data } = await getNotifications();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      // Silent fail — don't spam user with toasts on poll errors
    }
  }, [user]);

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchNotifs]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    setTimeout(() => document.addEventListener('click', onClick), 0);
    return () => document.removeEventListener('click', onClick);
  }, [open]);

  if (!user || !ELIGIBLE_ROLES.includes(user.role)) return null;

  const criticalCount = items.filter(n => n.severity === 'critical').length;
  const unreadCount = items.length;
  const hasCritical = criticalCount > 0;

  const handleClick = async (n) => {
    try {
      await markNotificationRead(n.id);
    } catch {}
    setItems(prev => prev.filter(x => x.id !== n.id));
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const handleClear = async (n, e) => {
    e.stopPropagation();
    try {
      await markNotificationRead(n.id);
    } catch {}
    setItems(prev => prev.filter(x => x.id !== n.id));
  };

  const handleClearAll = async () => {
    try {
      await markAllNotificationsRead();
      setItems([]);
      toast.success('Saari notifications cleared');
    } catch (err) {
      toast.error('Failed to mark all read');
    }
  };

  const sevColor = (s) => {
    switch (s) {
      case 'critical': return { dot: 'bg-red-600', bg: 'bg-red-50 hover:bg-red-100', border: 'border-l-red-500' };
      case 'high':     return { dot: 'bg-orange-500', bg: 'bg-orange-50 hover:bg-orange-100', border: 'border-l-orange-500' };
      case 'medium':   return { dot: 'bg-amber-500', bg: 'bg-amber-50 hover:bg-amber-100', border: 'border-l-amber-500' };
      default:         return { dot: 'bg-blue-500', bg: 'bg-white hover:bg-blue-50', border: 'border-l-blue-400' };
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diffM = (Date.now() - d.getTime()) / 60_000;
    if (diffM < 1) return 'just now';
    if (diffM < 60) return `${Math.floor(diffM)}m ago`;
    if (diffM < 1440) return `${Math.floor(diffM / 60)}h ago`;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' });
  };

  return (
    <div className="fixed top-3 right-4 z-50" ref={dropdownRef} data-testid="notification-bell">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className={`relative bg-white shadow-md rounded-full p-2 border ${hasCritical ? 'border-red-300' : 'border-gray-200'} hover:shadow-lg transition-shadow`}
        title={`${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`}
        data-testid="notification-bell-btn"
      >
        <Bell size={20} weight={hasCritical ? 'fill' : 'regular'} className={hasCritical ? 'text-red-600' : 'text-gray-700'} />
        {unreadCount > 0 && (
          <span
            className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center ${hasCritical ? 'bg-red-600 animate-pulse' : 'bg-blue-600'}`}
            data-testid="notification-count-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute top-12 right-0 w-96 max-h-[500px] bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
          data-testid="notification-dropdown"
        >
          <div className="px-4 py-3 border-b bg-gradient-to-r from-violet-50 to-blue-50 flex items-center justify-between">
            <div>
              <div className="text-sm font-bold text-gray-900">Notifications</div>
              <div className="text-[11px] text-gray-500">
                {unreadCount} unread · {criticalCount} critical
              </div>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleClearAll}
                className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
                data-testid="notif-mark-all-read"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {items.length === 0 ? (
              <div className="text-center py-8 px-4">
                <CheckCircle size={32} weight="fill" className="mx-auto text-emerald-400 mb-2" />
                <div className="text-sm text-gray-700 font-semibold">All caught up!</div>
                <div className="text-xs text-gray-500 mt-1">Koi pending notification nahi hai.</div>
              </div>
            ) : (
              items.map(n => {
                const c = sevColor(n.severity);
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-3 py-3 border-l-4 ${c.border} ${c.bg} border-b border-gray-100 transition-colors group flex items-start gap-2`}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <span className={`mt-1 w-2 h-2 rounded-full ${c.dot} shrink-0 ${n.severity === 'critical' ? 'animate-pulse' : ''}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-900 font-semibold leading-snug">
                        {n.message}
                      </div>
                      <div className="text-[10px] text-gray-500 mt-1 flex items-center gap-2">
                        <span>{formatTime(n.created_at)}</span>
                        {n.staff_name && <span>· {n.staff_name}</span>}
                        {n.severity === 'critical' && <span className="text-red-600 font-bold">CRITICAL</span>}
                      </div>
                    </div>
                    <span
                      onClick={(e) => handleClear(n, e)}
                      className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-700 p-0.5 cursor-pointer"
                      title="Dismiss"
                    >
                      <X size={14} />
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
